import type { z } from 'zod';

import type { Fetcher } from '@/lib/api/fetcher';

import type { Photo, PhotoKind, SignedUrl } from './schemas';
import { photoListSchema, photoSchema, signedUrlSchema } from './schemas';

/**
 * One function per BP-13 §5 route the §6 screens use.
 *
 * **Uploading needs the injected multipart sender**, not the shared fetcher:
 * `lib/api/fetcher` JSON-encodes every body it is given, which would destroy
 * a multipart boundary. BP-02 hit this first and solved it with a `FormData`
 * uploader; BP-13 needs one that carries two text fields beside the file, so
 * it takes a slightly wider function and the app supplies it.
 */
export type PhotoUploadFn = <T>(
  path: string,
  file: File,
  fields: Record<string, string>,
  schema: z.ZodType<T>,
) => Promise<T>;

export interface PhotosApi {
  listForSession: (sessionId: string) => Promise<Photo[]>;
  capture: (
    sessionId: string,
    file: File,
    body: { client_id: string; kind: PhotoKind },
  ) => Promise<Photo>;
  remove: (photoId: string) => Promise<void>;
  /** Staff: a fifteen-minute capability for one photograph. */
  signedUrl: (photoId: string) => Promise<SignedUrl>;
  /** The client's own gallery. */
  myPhotos: () => Promise<Photo[]>;
  /** The client's own capability, refused for a photograph that is not theirs. */
  mySignedUrl: (photoId: string) => Promise<SignedUrl>;
}

export const PREFIX = '/v1';

/** Build the §5 client over a `Fetcher` and a multipart sender. */
export function createPhotosApi(fetcher: Fetcher, upload: PhotoUploadFn): PhotosApi {
  return {
    listForSession: (sessionId) =>
      fetcher.json(`${PREFIX}/sessions/${sessionId}/photos`, { method: 'GET' }, photoListSchema),
    capture: (sessionId, file, body) =>
      upload(`${PREFIX}/sessions/${sessionId}/photos`, file, { ...body }, photoSchema),
    remove: (photoId) => fetcher.empty(`${PREFIX}/photos/${photoId}`, { method: 'DELETE' }),
    signedUrl: (photoId) =>
      fetcher.json(`${PREFIX}/photos/${photoId}/url`, { method: 'POST' }, signedUrlSchema),
    myPhotos: () => fetcher.json(`${PREFIX}/me/photos`, { method: 'GET' }, photoListSchema),
    mySignedUrl: (photoId) =>
      fetcher.json(`${PREFIX}/me/photos/${photoId}/url`, { method: 'POST' }, signedUrlSchema),
  };
}
